<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WahaAPI from 'dashboard/api/waha';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

// Emitted so the parent can refetch the inbox — once attached, is_waha
// becomes true and the WAHA tab (session status/actions/QR) should appear.
const emit = defineEmits(['attached']);

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const sessionName = ref('');
const isSubmitting = ref(false);

const attach = async () => {
  const trimmedName = sessionName.value.trim();
  if (!trimmedName || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    await WahaAPI.attachExistingSession(
      store.getters.getCurrentAccountId,
      props.inbox.id,
      trimmedName
    );
    showAlert(t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SUCCESS'));
    sessionName.value = '';
    emit('attached');
  } catch (error) {
    const backendMessage = error?.response?.data?.error;
    showAlert(
      backendMessage || t('INBOX_MGMT.WAHA.ATTACH_EXISTING.GENERIC_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.WAHA.ATTACH_EXISTING.TITLE')"
    :sub-title="$t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SUBTITLE')"
  >
    <div class="flex flex-col gap-4 sm:flex-row sm:items-end">
      <div class="flex-1">
        <label class="mb-1 text-sm font-medium text-n-slate-11">
          {{ $t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SESSION_NAME_LABEL') }}
        </label>
        <Input
          v-model="sessionName"
          :placeholder="
            $t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SESSION_NAME_PLACEHOLDER')
          "
          class="!mb-0"
          @keyup.enter="attach"
        />
      </div>
      <NextButton
        :label="
          isSubmitting
            ? $t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SUBMITTING')
            : $t('INBOX_MGMT.WAHA.ATTACH_EXISTING.SUBMIT_BUTTON')
        "
        :disabled="!sessionName.trim() || isSubmitting"
        class="flex-shrink-0"
        @click="attach"
      />
    </div>
  </SettingsSection>
</template>
